import type { TStoreName } from "../types/domain/store";
import { buildDocument } from "./build-document";
import { APP_NAME, STORE_SUFFIX } from "./store";

/** 스토어별로 다른 쿼리만 키로 두고, 나머지는 default 에 `${S}` 보간으로 한 벌만 유지한다. */
const withFragments = (operations: Partial<Record<TStoreName, string>> & { default: string }) =>
  buildDocument(operations[APP_NAME] ?? operations.default);

const S = STORE_SUFFIX;

/** 블로그 아티클 목록 + 커서 목록 */
export const blogQuery = withFragments({
  default: `#graphql
    query Blog${S}($handle: String, $first: Int, $after: String, $sortKey: ArticleSortKeys, $reverse: Boolean, $query: String, $cursor_first: Int, $cursor_sortKey: ArticleSortKeys, $cursor_query: String) {
      blog(handle: $handle) {
        articles(first: $first, after: $after, sortKey: $sortKey, reverse: $reverse, query: $query) {
          edges {
            node {
              ...ArticleBaseFields
              ...BlogCursorMetafields${S}
            }
          }
          pageInfo { ...PageInfoFields }
        }
        cursor_list: articles(first: $cursor_first, sortKey: $cursor_sortKey, query: $cursor_query) {
          edges {
            cursor
            node {
              ...ArticleBaseFields
              ...BlogCursorMetafields${S}
            }
          }
          pageInfo { ...PageInfoFields }
        }
      }
    }
  `,
  khakis: `#graphql
    query BlogKhakis($handle: String, $first: Int, $after: String, $sortKey: ArticleSortKeys, $reverse: Boolean, $query: String) {
      blog(handle: $handle) {
        articles(first: $first, after: $after, sortKey: $sortKey, reverse: $reverse, query: $query) {
          edges {
            node {
              ...BlogArticleCardFieldsKhakis
            }
          }
          pageInfo { ...PageInfoFields }
        }
      }
    }
  `,
  thisisneverthat: `#graphql
    query BlogThisisneverthat($handle: String, $first: Int, $after: String, $sortKey: ArticleSortKeys, $reverse: Boolean, $query: String, $cursor_first: Int, $cursor_sortKey: ArticleSortKeys, $cursor_query: String) {
      blog(handle: $handle) {
        articles(first: $first, after: $after, sortKey: $sortKey, reverse: $reverse, query: $query) {
          edges {
            node {
              ...ArticleBaseFields
              ...BlogArticleMetafieldsThisisneverthat
            }
          }
          pageInfo { ...PageInfoFields }
        }
        cursor_list: articles(first: $cursor_first, sortKey: $cursor_sortKey, query: $cursor_query) {
          edges {
            cursor
            node {
              ...ArticleBaseFields
              ...BlogCursorMetafieldsThisisneverthat
            }
          }
          pageInfo { ...PageInfoFields }
        }
      }
    }
  `,
  sunlove: `#graphql
    query BlogSunlove($handle: String, $first: Int, $after: String, $sortKey: ArticleSortKeys, $reverse: Boolean, $query: String) {
      blog(handle: $handle) {
        articles(first: $first, after: $after, sortKey: $sortKey, reverse: $reverse, query: $query) {
          edges {
            node {
              ...BlogArticleCardFieldsSunlove
            }
          }
          pageInfo { ...PageInfoFields }
        }
      }
    }
  `,
  wellbeingexpress: `#graphql
    query BlogWellbeingexpress($handle: String, $first: Int, $after: String, $sortKey: ArticleSortKeys, $reverse: Boolean, $query: String, $cursor_first: Int, $cursor_sortKey: ArticleSortKeys, $cursor_query: String) {
      blog(handle: $handle) {
        articles(first: $first, after: $after, sortKey: $sortKey, reverse: $reverse, query: $query) {
          edges {
            node {
              ...ArticleBaseFields
              ...BlogArticleMetafieldsWellbeingexpress
            }
          }
          pageInfo { ...PageInfoFields }
        }
        cursor_list: articles(first: $cursor_first, sortKey: $cursor_sortKey, query: $cursor_query) {
          edges {
            cursor
            node {
              ...ArticleBaseFields
              ...BlogCursorMetafieldsWellbeingexpress
            }
          }
          pageInfo { ...PageInfoFields }
        }
      }
    }
  `,
});

/** handle로 아티클 단건 조회 + 커서 목록 (이전/다음 글 이동용) */
export const blogArticleQuery = withFragments({
  default: `#graphql
    query BlogArticleByHandle${S}($handle: String, $articleHandle: String!, $cursor_first: Int, $cursor_sortKey: ArticleSortKeys, $cursor_query: String) {
      blog(handle: $handle) {
        articleByHandle(handle: $articleHandle) {
          ...ArticleBaseFields
          ...BlogCursorMetafields${S}
        }
        cursor_list: articles(first: $cursor_first, sortKey: $cursor_sortKey, query: $cursor_query) {
          edges {
            cursor
            node {
              ...ArticleBaseFields
              ...BlogCursorMetafields${S}
            }
          }
          pageInfo { ...PageInfoFields }
        }
      }
    }
  `,
  khakis: `#graphql
    query BlogArticleByHandleKhakis($handle: String, $articleHandle: String!) {
      blog(handle: $handle) {
        articleByHandle(handle: $articleHandle) {
          ...ArticleBaseFields
          ...BlogCursorMetafieldsKhakis
        }
      }
    }
  `,
  thisisneverthat: `#graphql
    query BlogArticleByHandleThisisneverthat($handle: String, $articleHandle: String!, $cursor_first: Int, $cursor_sortKey: ArticleSortKeys, $cursor_query: String) {
      blog(handle: $handle) {
        articleByHandle(handle: $articleHandle) {
          ...ArticleBaseFields
          ...BlogArticleMetafieldsThisisneverthat
          ...BlogArticleDetailMetafieldsThisisneverthat
        }
        cursor_list: articles(first: $cursor_first, sortKey: $cursor_sortKey, query: $cursor_query) {
          edges {
            cursor
            node {
              ...ArticleBaseFields
              ...BlogCursorMetafieldsThisisneverthat
            }
          }
          pageInfo { ...PageInfoFields }
        }
      }
    }
  `,
  sunlove: `#graphql
    query BlogArticleByHandleSunlove($handle: String, $articleHandle: String!) {
      blog(handle: $handle) {
        articleByHandle(handle: $articleHandle) {
          ...ArticleBaseFields
          ...BlogArticleMetafieldsSunlove
        }
      }
    }
  `,
  wellbeingexpress: `#graphql
    query BlogArticleByHandleWellbeingexpress($handle: String, $articleHandle: String!, $cursor_first: Int, $cursor_sortKey: ArticleSortKeys, $cursor_query: String) {
      blog(handle: $handle) {
        articleByHandle(handle: $articleHandle) {
          ...ArticleBaseFields
          ...BlogArticleMetafieldsWellbeingexpress
        }
        cursor_list: articles(first: $cursor_first, sortKey: $cursor_sortKey, query: $cursor_query) {
          edges {
            cursor
            node {
              ...ArticleBaseFields
              ...BlogCursorMetafieldsWellbeingexpress
            }
          }
          pageInfo { ...PageInfoFields }
        }
      }
    }
  `,
});

/**
 * 이전/다음 이동·필터 집계용 커서 목록만 따로 조회한다.
 * 목록/상세 쿼리에 얹어두면 아티클 handle 마다 캐시 키가 갈려서 같은 목록을 매번 다시 받게 되므로,
 * variables 가 같으면 목록/상세가 같은 캐시(staleTime: Infinity)를 공유하도록 쿼리를 분리했다.
 */
export const blogCursorListQuery = withFragments({
  default: `#graphql
    query BlogCursorList${S}($handle: String, $cursor_first: Int, $cursor_sortKey: ArticleSortKeys, $cursor_query: String) {
      blog(handle: $handle) {
        cursor_list: articles(first: $cursor_first, sortKey: $cursor_sortKey, query: $cursor_query) {
          edges {
            cursor
            node {
              ...ArticleBaseFields
              ...BlogCursorMetafields${S}
            }
          }
          pageInfo { ...PageInfoFields }
        }
      }
    }
  `,
  sunlove: `#graphql
    query BlogCursorListSunlove($handle: String, $cursor_first: Int, $cursor_sortKey: ArticleSortKeys, $cursor_query: String) {
      blog(handle: $handle) {
        cursor_list: articles(first: $cursor_first, sortKey: $cursor_sortKey, query: $cursor_query) {
          edges {
            cursor
            node {
              ...BlogCursorFieldsSunlove
            }
          }
          pageInfo { ...PageInfoFields }
        }
      }
    }
  `,
  khakis: `#graphql
    query BlogCursorListKhakis($handle: String, $cursor_first: Int, $cursor_sortKey: ArticleSortKeys, $cursor_query: String) {
      blog(handle: $handle) {
        cursor_list: articles(first: $cursor_first, sortKey: $cursor_sortKey, query: $cursor_query) {
          edges {
            cursor
            node {
              ...BlogCursorFieldsKhakis
            }
          }
          pageInfo { ...PageInfoFields }
        }
      }
    }
  `,
});

/**
 * 여러 blog 를 한 번에 조회 (Features 통합 페이지용).
 * articles 는 connection 이라 first 가 필수라서 바깥 blogs 의 $first 와 겹치지 않게 별도 변수명을 쓴다.
 * 아티클이 더 남았는지는 blog 마다 따로 봐야 하므로 blogs 레벨 pageInfo 와 별개로 articles.pageInfo 도 받는다.
 */
export const blogsQuery = withFragments({
  default: `#graphql
    query Blogs${S}($first: Int, $after: String, $reverse: Boolean, $query: String, $articles_first: Int, $articles_sortKey: ArticleSortKeys, $articles_reverse: Boolean, $articles_query: String) {
      blogs(first: $first, after: $after, reverse: $reverse, query: $query) {
        edges {
          node {
            handle
            articles(first: $articles_first, sortKey: $articles_sortKey, reverse: $articles_reverse, query: $articles_query) {
              edges {
                node {
                  ...ArticleBaseFields
                  ...BlogCursorMetafields${S}
                }
              }
              pageInfo { ...PageInfoFields }
            }
          }
        }
        pageInfo { ...PageInfoFields }
      }
    }
  `,
  thisisneverthat: `#graphql
    query BlogsThisisneverthat($first: Int, $after: String, $reverse: Boolean, $query: String, $articles_first: Int, $articles_sortKey: ArticleSortKeys, $articles_reverse: Boolean, $articles_query: String) {
      blogs(first: $first, after: $after, reverse: $reverse, query: $query) {
        edges {
          node {
            handle
            articles(first: $articles_first, sortKey: $articles_sortKey, reverse: $articles_reverse, query: $articles_query) {
              edges {
                node {
                  ...ArticleBaseFields
                  ...BlogArticleMetafieldsThisisneverthat
                }
              }
              pageInfo { ...PageInfoFields }
            }
          }
        }
        pageInfo { ...PageInfoFields }
      }
    }
  `,
  sunlove: `#graphql
    query BlogsSunlove($first: Int, $after: String, $reverse: Boolean, $query: String, $articles_first: Int, $articles_sortKey: ArticleSortKeys, $articles_reverse: Boolean, $articles_query: String) {
      blogs(first: $first, after: $after, reverse: $reverse, query: $query) {
        edges {
          node {
            handle
            articles(first: $articles_first, sortKey: $articles_sortKey, reverse: $articles_reverse, query: $articles_query) {
              edges {
                node {
                  ...ArticleBaseFields
                }
              }
              pageInfo { ...PageInfoFields }
            }
          }
        }
        pageInfo { ...PageInfoFields }
      }
    }
  `,
  wellbeingexpress: `#graphql
    query BlogsWellbeingexpress($first: Int, $after: String, $reverse: Boolean, $query: String, $articles_first: Int, $articles_sortKey: ArticleSortKeys, $articles_reverse: Boolean, $articles_query: String) {
      blogs(first: $first, after: $after, reverse: $reverse, query: $query) {
        edges {
          node {
            handle
            articles(first: $articles_first, sortKey: $articles_sortKey, reverse: $articles_reverse, query: $articles_query) {
              edges {
                node {
                  ...ArticleBaseFields
                  ...BlogArticleMetafieldsWellbeingexpress
                }
              }
              pageInfo { ...PageInfoFields }
            }
          }
        }
        pageInfo { ...PageInfoFields }
      }
    }
  `,
});
